import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Pagination,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from "@mui/material";
import {
  ArrowBack,
  Phone,
  VideoCall,
  Edit,
  Save,
  Cancel,
  Schedule,
  Person,
  Email,
  LocationOn,
  AccessTime,
  CalendarToday,
  ExpandMore,
  Send,
  NavigateBefore,
  NavigateNext,
  Chat,
  Note,
  Assessment,
  Psychology,
  MedicalServices,
  LocalHospital,
  FitnessCenter,
  Spa,
  Restaurant,
  School,
  Work,
  Family,
  Favorite,
  Star,
  Warning,
  CheckCircle,
  Cancel as CancelIcon,
  Reschedule,
  Close,
  Visibility,
  Refresh,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../auth";
import { handleBrokenImage } from "../../lib/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to safely format dates with timezone support
const formatDateSafely = (dateString, format = "DD MMM YYYY hh:mm A") => {
  if (!dateString) {
    return "N/A";
  }

  try {
    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // First, try to parse as UTC/ISO format
    const utcFormats = [
      "YYYY-MM-DDTHH:mm:ssZ", // ISO format with Z
      "YYYY-MM-DDTHH:mm:ss", // ISO format without Z
      "YYYY-MM-DD HH:mm:ss", // Simple UTC format
      "DD MMM YYYY HH:mm", // API format (assuming UTC)
      "DD MMM YYYY HH:mm:ss", // API format with seconds
      "DD MMM YYYY hh:mm A", // API format 12-hour
    ];

    // Try parsing as UTC first
    for (const fmt of utcFormats) {
      const parsed = dayjs.utc(dateString, fmt);
      if (parsed.isValid()) {
        // Convert to local time
        return parsed.tz(userTimezone).format(format);
      }
    }

    // If not UTC, try as local time formats
    const localFormats = [
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY hh:mm A",
      "DD-MM-YYYY HH:mm",
      "DD-MM-YYYY hh:mm A",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD hh:mm A",
    ];

    // Try each local format
    for (const fmt of localFormats) {
      const parsed = dayjs(dateString, fmt);
      if (parsed.isValid()) {
        return parsed.format(format);
      }
    }

    // Last resort: try parsing as-is
    const parsedDate = dayjs(dateString);
    if (parsedDate.isValid()) {
      // Assume it's UTC if it has specific markers
      if (
        dateString.includes("T") ||
        dateString.includes("Z") ||
        dateString.includes("+")
      ) {
        return parsedDate.tz(userTimezone).format(format);
      }
      return parsedDate.format(format);
    }

    console.warn("Invalid date format:", dateString);
    return "Invalid Date";
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return "Invalid Date";
  }
};

const AppointmentDetailsViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // State management
  const [appointmentData, setAppointmentData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [existingComments, setExistingComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [totalCommentsPages, setTotalCommentsPages] = useState(1);

  // Cancel and Reschedule states
  const [openCancelConfirmationModal, setOpenCancelConfirmationModal] =
    useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [isCancelled, setIsCancelled] = useState(null);
  const [isRescheduled, setIsRescheduled] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    appointmentDate: "",
    appointmentTime: "",
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Get URL params for fallback and navigation state
  const searchParams = new URLSearchParams(location.search);
  const patientName = location.state?.patientName || searchParams.get("patient");
  const clientId = location.state?.clientId || searchParams.get("clientId") || searchParams.get("id") || searchParams.get("patientId");
  
  // If we have appointment data from navigation state, use it
  const navigationAppointmentData = location.state?.appointmentData;

  useEffect(() => {
    // If we have complete appointment data from navigation, use it directly
    if (navigationAppointmentData && navigationAppointmentData.ID) {
      setAppointmentData(navigationAppointmentData);
      
      // Extract patient info from navigation data (already available from parent)
      const patientInfo = {
        name: navigationAppointmentData.Name || patientName || "Patient",
        id: navigationAppointmentData.UserId || clientId, // Patient ID is already available from parent
        email: navigationAppointmentData.Email || "",
        phone: navigationAppointmentData.MobileNo || "",
        age: navigationAppointmentData.Age || "",
        gender: navigationAppointmentData.Gender || "",
        profileImage: navigationAppointmentData.ProfileImage || "",
      };
      
      setPatientData(patientInfo);
      setLoading(false);
    } else {
      // Fallback to API call only if no navigation data is available
      fetchAppointmentDetails();
    }


  }, [id, navigationAppointmentData]);

  // Fetch comments when patient data is available
  useEffect(() => {
    const patientId = patientData?.id || clientId;
    const expertId = currentUser?.UserId;
    const appointmentId = id;

    if (patientId && expertId && appointmentId) {
      fetchComments();
    }
  }, [patientData?.id, clientId, currentUser?.UserId, id]);



  const fetchComments = async (page = 1) => {
    try {
      setLoadingComments(true);

              // Ensure we have valid IDs for the API call
        const patientId = parseInt(patientData?.id) || parseInt(clientId) || 0;
        const expertId = parseInt(currentUser?.UserId) || 0;
        const appointmentId = parseInt(id) || 0;

        // Validate that we have all required parameters
        if (!patientId || !expertId || !appointmentId) {
          setExistingComments([]);
          return;
        }

      // Build query parameters with pagination
      const params = new URLSearchParams({
        patient_id: patientId,
        expert_id: expertId,
        appointment_id: appointmentId,
        page: page.toString(),
        count: "10", // Default count per page
      });

      const response = await fetch(
        `https://us.uwc.world/new-uwc/expert/dashboard/get-expert-comments?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Handle the new API response structure
        if (
          result &&
          result.code === 200 &&
          result.data &&
          result.data.comments &&
          Array.isArray(result.data.comments)
        ) {
          const transformedComments = result.data.comments.map((comment) => ({
            id: comment.id || Date.now(),
            text: comment.comment || comment.text || "",
            author:
              comment.expert?.name ||
              comment.expert_name ||
              comment.author ||
              currentUser?.Name ||
              "Expert",
            timestamp: (() => {
              const rawDate = comment.created_at || comment.timestamp;
              if (typeof rawDate === "string") {
                let cleanDate = rawDate;
                const match = rawDate.match(/(\d{2}):(\d{2}) (AM|PM)/);
                if (match) {
                  const hour = parseInt(match[1]);
                  const minutes = match[2];
                  if (hour > 12) {
                    const hour12 = hour === 12 ? 12 : hour % 12;
                    cleanDate = rawDate.replace(
                      `${hour}:${minutes}`,
                      `${hour12}:${minutes}`
                    );
                  }
                }
                const utcDate = dayjs.utc(cleanDate, "DD MMM YYYY hh:mm A");
                if (utcDate.isValid()) {
                  return utcDate
                    .tz("Asia/Calcutta")
                    .format("DD MMM YYYY hh:mm A");
                }
              }
              return rawDate || "Invalid Date";
            })(),
            type: comment.type || "comment",
            expertImage: comment.expert?.image || "",
            patientImage: comment.patient?.image || "",
          }));

          setExistingComments(transformedComments);
          setTotalCommentsPages(result.data.total_page_count || 1);
          setCommentsPage(page);
        } else {
          setExistingComments([]);
          setTotalCommentsPages(1);
        }
      } else {
        setExistingComments([]);
        setTotalCommentsPages(1);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setExistingComments([]);
      setTotalCommentsPages(1);
    } finally {
      setLoadingComments(false);
    }
  };

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);

      const headers = {
        "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
        "Content-Type": "application/json",
      };

      const payload = {
        expert_id: currentUser?.UserId,
        appointment_id: id,
      };

      const response = await fetch(
        `https://odoo.unitedwecare.ca/new-uwc/expert/dashboard/appointments`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const postResponse = await response.json();

      if (postResponse?.data?.appointments) {
        const foundAppointment = postResponse.data.appointments.find(
          (o) => o.ID == id
        );
        if (foundAppointment) {
          setAppointmentData(foundAppointment);

          // Try multiple possible patient ID fields from the appointment data
          const possiblePatientIds = [
            foundAppointment.UserId,
            foundAppointment.ClientId,
            foundAppointment.PatientId,
            foundAppointment.patient_id,
            foundAppointment.user_id,
            foundAppointment.client_id,
            clientId
          ];
          
          const patientId = possiblePatientIds.find(id => id && id !== "Unknown" && id !== "" && id !== null && id !== undefined) || "Unknown";

          const patientInfo = {
            name: foundAppointment.Name || patientName || "Patient",
            id: patientId,
            email: foundAppointment.Email || "",
            phone: foundAppointment.MobileNo || "",
            age: foundAppointment.Age || "",
            gender: foundAppointment.Gender || "",
            profileImage: foundAppointment.ProfileImage || "",
          };

          setPatientData(patientInfo);
        } else {
          throw new Error("Appointment not found");
        }
      } else {
        throw new Error("Invalid API response");
      }
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      enqueueSnackbar("Failed to load appointment details", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewJourney = () => {
    // Navigate to patient journey page
    navigate(
      `/expert/patients?patient=${patientData?.name}&id=${patientData?.id}&tabIndex=0`
    );
  };

  const handleJoinCall = () => {
    if (appointmentData?.join_url) {
      window.open(appointmentData.join_url, "_blank");
    }
  };

  const handleSendComment = async () => {
    if (comment.trim()) {
      setIsSubmittingComment(true);
      try {
        // Ensure we have valid IDs for the API call
        const patientId = parseInt(patientData?.id) || parseInt(clientId) || 0;
        const expertId = parseInt(currentUser?.UserId) || 0;
        const appointmentId =
          parseInt(appointmentData?.ID) || parseInt(id) || 0;

        // Validate that we have all required parameters
        if (!patientId || !expertId || !appointmentId) {
          enqueueSnackbar(
            "Missing required information. Please refresh the page.",
            { variant: "error" }
          );
          return;
        }

        const payload = {
          patient_id: patientId,
          expert_id: expertId,
          comment: comment.trim(),
          appointment_id: appointmentId,
        };

        const response = await fetch(
          "https://us.uwc.world/new-uwc/expert/dashboard/add-expert-comment",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();

          // Add a small delay to ensure server has processed the comment
          setTimeout(async () => {
            await fetchComments(1); // Refresh to first page
          }, 1000);

          setComment("");
          enqueueSnackbar("Comment added successfully", { variant: "success" });
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      } catch (error) {
        console.error("Error adding comment:", error);
        enqueueSnackbar("Failed to add comment. Please try again.", {
          variant: "error",
        });
      } finally {
        setIsSubmittingComment(false);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendComment();
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (commentsPage > 1) {
      fetchComments(commentsPage - 1);
    }
  };

  const handleNextPage = () => {
    if (commentsPage < totalCommentsPages) {
      fetchComments(commentsPage + 1);
    }
  };

  // Cancel Appointment Functions
  const handleCancelConfirmationOpen = () => {
    setOpenCancelConfirmationModal(true);
  };

  const handleCancelConfirmationClose = () => {
    setOpenCancelConfirmationModal(false);
  };

  const handleCancel = async () => {
    setIsCancelled(false);

    const payload = {
      uwc_appointmentid: appointmentData?.ID,
    };

    const cancelHeaders = {
      "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      "Content-Type": "application/json",
    };

    try {
      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/cancel`,
        {
          method: "POST",
          headers: cancelHeaders,
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        handleCancelConfirmationClose();
        enqueueSnackbar("Appointment cancelled successfully!", {
          variant: "success",
        });
        setIsCancelled(true);
        // Update appointment status locally
        setAppointmentData((prev) => ({ ...prev, Status: "Cancelled" }));
      } else {
        enqueueSnackbar("Failed to cancel appointment", { variant: "error" });
        setIsCancelled(true);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Error occurred while cancelling appointment", {
        variant: "error",
      });
      setIsCancelled(true);
    }
  };

  // Reschedule Appointment Functions
  const handleRescheduleOpen = () => {
    // Set today's date as default for reschedule
    const todayDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    setRescheduleData({
      appointmentDate: dayjs(todayDate).format("DD/MM/YYYY"), // Display format
      appointmentTime: "", // Don't pre-select time
    });
    setOpenRescheduleModal(true);
    // Don't automatically fetch slots - wait for user to select date
  };

  const handleRescheduleClose = () => {
    setOpenRescheduleModal(false);
  };

  const onDateChange = (date) => {
    // date is already in YYYY-MM-DD format from the input
    if (!date) {
      setAvailableSlots([]);
      setRescheduleData((prev) => ({
        ...prev,
        appointmentDate: "",
        appointmentTime: "",
      }));
      return;
    }

    const formattedDate = dayjs(date).format("DD/MM/YYYY");

    setRescheduleData((prev) => ({
      ...prev,
      appointmentDate: formattedDate,
      appointmentTime: "", // Clear selected time when date changes
    }));

    // Clear existing slots when date changes
    setAvailableSlots([]);

    // Fetch available slots for the selected date - use the original YYYY-MM-DD format
    fetchAvailableSlots(date);
  };

  const onTimeChange = (time) => {
    setRescheduleData((prev) => ({
      ...prev,
      appointmentTime: time,
    }));
  };

  const fetchAvailableSlots = async (date) => {
    try {
      setLoadingSlots(true);

      // Use expert ID from appointment details - it's in the ExpertId field
      const expertId = appointmentData?.ExpertId || "";

      if (!expertId) {
        setAvailableSlots([]);
        return;
      }

      // date is already in YYYY-MM-DD format
      const formattedDate = date;

      // Build query parameters - using the same format as BookAppointmentsModal
      const params = new URLSearchParams({
        expert_id: expertId.toString(),
        date: formattedDate,
      });

      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/slots?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // Process the slots data based on the actual API response format
      if (data.data && data.data.slots && data.data.slots[formattedDate]) {
        // Extract slots for the selected date
        const dateSlots = data.data.slots[formattedDate];

        // Convert datetime strings to 12-hour time format for display
        const formattedSlots = dateSlots.map((slot) => {
          const datetime = new Date(slot.time);
          const hours = datetime.getHours();
          const minutes = datetime.getMinutes();
          const ampm = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          const displayMinutes = minutes.toString().padStart(2, "0");
          return `${displayHour}:${displayMinutes} ${ampm}`;
        });

        setAvailableSlots(formattedSlots);
      } else {
        // If no slots available for the selected date
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleRescheduleAppointment = async () => {
    setIsRescheduled(false);

    // Format the appointment date and time for the API
    const formattedDate = moment(rescheduleData.appointmentDate, [
      "DD/MM/YYYY",
      "DD MMM YYYY",
    ]).format("YYYY-MM-DD");
    const formattedTime = moment(
      rescheduleData.appointmentTime,
      "hh:mm A"
    ).format("HH:mm:ss");
    const appointmentDateTime = `${formattedDate} ${formattedTime}`;

    // Get timezone from browser or use default
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "US/Pacific";

    // Extract duration from appointment data
    const duration = parseInt(appointmentData?.duration?.split(" ")[0]) || 30;

    const payload = {
      uwc_appointmentid: appointmentData?.ID,
      appointment_time: appointmentDateTime,
      timezone: timezone,
      duration: duration,
    };

    const rescheduleHeaders = {
      "Content-Type": "application/json",
      "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
    };

    try {
      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/reschedule`,
        {
          method: "POST",
          headers: rescheduleHeaders,
          body: JSON.stringify(payload),
        }
      );
      const postResponse = await response.json();

      if (
        postResponse.code === 200 ||
        postResponse.success ||
        postResponse.status === 200 ||
        postResponse.ResponseCode === 200
      ) {
        enqueueSnackbar("Appointment rescheduled successfully!", {
          variant: "success",
        });
        handleRescheduleClose();
        setIsRescheduled(true);

        // Update appointment data locally
        setAppointmentData((prev) => ({
          ...prev,
          AppDate: rescheduleData.appointmentDate,
          AppTime: rescheduleData.appointmentTime,
        }));
      } else {
        enqueueSnackbar("Failed to reschedule appointment", {
          variant: "error",
        });
        setIsRescheduled(true);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Error occurred while rescheduling appointment", {
        variant: "error",
      });
      setIsRescheduled(true);
    }
  };

  // Check if appointment can be cancelled or rescheduled
  const canCancelOrReschedule = () => {
    if (!appointmentData) return false;

    const status = appointmentData.Status?.toLowerCase();
    if (status === "cancelled" || status === "completed") return false;

    // Check if appointment is in the past
    const appointmentDateTime = moment(
      `${appointmentData.AppDate} ${appointmentData.AppTime}`,
      ["DD/MM/YYYY hh:mm A", "DD MMM YYYY hh:mm A"]
    );
    if (appointmentDateTime.isBefore(moment())) return false;

    return true;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return { bg: "#DCFAE6", color: "#079455" };
      case "cancelled":
        return { bg: "#FEE4E2", color: "#D92D20" };
      case "upcoming":
        return { bg: "#ECE8F2", color: "#654E89" };
      case "open":
        return { bg: "#E8F4FD", color: "#0F676E" };
      default:
        return { bg: "#F5F5F5", color: "#666666" };
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Loading appointment details...
          </Typography>
        </Box>
      </Container>
    );
  }

  const statusColors = getStatusColor(appointmentData?.Status);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            mr: 2,
            color: "var(--primary-base-color)",
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: "var(--primary-base-color)" }}
        >
          Appointment Details
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        {/* Patient Information Card */}
        <Grid item xs={12} lg={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              boxShadow: "3px 6px 20px rgba(93, 134, 194, 0.12)",
              position: "relative",
              overflow: "visible",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "25px",
                left: 0,
                width: "6px",
                background: "var(--primary-base-color)",
                height: "40px",
                borderRadius: "0px 10px 10px 0px",
              }}
            />
            <CardContent
              sx={{
                pb: 2,
                pt: 3,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "start", gap: 2, mb: 3 }}>
                <Avatar
                  src={patientData?.profileImage}
                  onError={(e) => {
                    const newLink = handleBrokenImage(
                      patientData?.profileImage
                    );
                    e.target.src = newLink;
                  }}
                  sx={{ width: 80, height: 80 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#cbe3fb",
                        color: "#002850",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                      }}
                    >
                      {appointmentData?.client_type || "Patient"}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    {patientData?.name || patientName || "Patient"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Patient ID: {patientData?.id || clientId || "Unknown"}
                  </Typography>
                  {patientData?.age && (
                    <Typography variant="body2" color="text.secondary">
                      Age: {patientData.age} years
                    </Typography>
                  )}
                  {patientData?.gender && (
                    <Typography variant="body2" color="text.secondary">
                      Gender: {patientData.gender}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Contact Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "var(--primary-base-color)",
                  }}
                >
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  {patientData?.phone && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Phone sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography variant="body2">
                          {patientData.phone}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {patientData?.email && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Email sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography variant="body2">
                          {patientData.email}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {appointmentData?.Country && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationOn
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                        <Typography variant="body2">
                          {appointmentData.Country}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {!patientData?.email &&
                  !patientData?.phone &&
                  !appointmentData?.Country && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      Contact information not available
                    </Typography>
                  )}
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: "auto",
                  pt: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={handleViewJourney}
                  sx={{
                    bgcolor: "var(--primary-base-color)",
                    color: "white",
                    borderRadius: "10px",
                    textTransform: "capitalize",
                    "&:hover": {
                      bgcolor: "var(--primary-base-color-darken)",
                    },
                  }}
                >
                  View
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Details Card */}
        <Grid item xs={12} lg={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              boxShadow: "3px 6px 20px rgba(93, 134, 194, 0.12)",
              position: "relative",
              overflow: "visible",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "25px",
                left: 0,
                width: "6px",
                background: statusColors.color,
                height: "40px",
                borderRadius: "0px 10px 10px 0px",
              }}
            />
            <CardContent
              sx={{
                pb: 2,
                pt: 3,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Appointment Details
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: statusColors.bg,
                    color: statusColors.color,
                    padding: "6px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {appointmentData?.Status || "Unknown"}
                </Typography>
              </Box>

              {/* Appointment Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarToday
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: "500" }}>
                      {appointmentData?.AppDate ||
                        appointmentData?.AppointmentDate ||
                        "Date not available"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTime
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: "500" }}>
                      {appointmentData?.AppTime
                        ? `${appointmentData.AppTime} (${
                            appointmentData.TimeZone || "IST"
                          })`
                        : appointmentData?.AppointmentTime
                        ? `${appointmentData.AppointmentTime} (${
                            appointmentData.TimeZone || "IST"
                          })`
                        : "Time not available"}
                    </Typography>
                  </Box>
                </Grid>
                {appointmentData?.duration && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {appointmentData.duration}
                    </Typography>
                  </Grid>
                )}
                {appointmentData?.CallingMode && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Mode: {appointmentData.CallingMode}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Expert Information */}
              {appointmentData?.ExpertName && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: "var(--primary-base-color)",
                    }}
                  >
                    Expert Information
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {appointmentData.ExpertName}
                  </Typography>
                  {appointmentData?.BookedRegisterAs && (
                    <Chip
                      label={appointmentData.BookedRegisterAs}
                      variant="outlined"
                      sx={{
                        color: "#0F676E",
                        borderColor: "#0F676E",
                        backgroundColor: "#8DEAFC30",
                        textTransform: "capitalize",
                        fontSize: "12px",
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  mt: "auto",
                  pt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {appointmentData?.Status === "Open" &&
                  appointmentData?.join_url && (
                    <Button
                      variant="contained"
                      startIcon={<VideoCall />}
                      onClick={handleJoinCall}
                      fullWidth
                      sx={{
                        bgcolor: "var(--primary-base-color)",
                        color: "white",
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        py: 1.5,
                        "&:hover": {
                          bgcolor: "var(--primary-base-color-darken)",
                        },
                      }}
                    >
                      Join Call
                    </Button>
                  )}

                {canCancelOrReschedule() && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Schedule />}
                      onClick={handleRescheduleOpen}
                      sx={{
                        flex: 1,
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        borderColor: "var(--primary-base-color)",
                        color: "var(--primary-base-color)",
                        "&:hover": {
                          borderColor: "var(--primary-base-color-darken)",
                          bgcolor: "rgba(15, 103, 110, 0.04)",
                        },
                      }}
                    >
                      Reschedule
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancelConfirmationOpen}
                      sx={{
                        flex: 1,
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        borderColor: "#f57e8c",
                        color: "#f57e8c",
                        "&:hover": {
                          borderColor: "#d32f2f",
                          bgcolor: "rgba(245, 126, 140, 0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Comments Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              boxShadow: "3px 6px 20px rgba(93, 134, 194, 0.12)",
              position: "relative",
              overflow: "visible",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "25px",
                left: 0,
                width: "6px",
                background: "#FFA726",
                height: "40px",
                borderRadius: "0px 10px 10px 0px",
              }}
            />
            <CardContent sx={{ pb: 2, pt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Comments
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={()=>{fetchComments()}}
                  disabled={loadingComments}
                  size="small"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "capitalize",
                    borderColor: "var(--primary-base-color)",
                    color: "var(--primary-base-color)",
                    "&:hover": {
                      borderColor: "var(--primary-base-color-darken)",
                      bgcolor: "rgba(15, 103, 110, 0.04)",
                    },
                  }}
                >
                  {loadingComments ? "Loading..." : "Refresh"}
                </Button>
              </Box>

              {/* Add Comment Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "var(--primary-base-color)",
                  }}
                >
                  Add Note or Comment
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your note or comment here..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyPress={handleKeyPress}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--primary-base-color)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--primary-base-color)",
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendComment}
                    disabled={!comment.trim() || isSubmittingComment}
                    startIcon={
                      isSubmittingComment ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Send />
                      )
                    }
                    sx={{
                      bgcolor: "var(--primary-base-color)",
                      color: "white",
                      borderRadius: "10px",
                      textTransform: "capitalize",
                      px: 3,
                      py: 1.5,
                      minWidth: "auto",
                      "&:hover": {
                        bgcolor: "var(--primary-base-color-darken)",
                      },
                      "&:disabled": {
                        bgcolor: "#ccc",
                        color: "#666",
                      },
                    }}
                  >
                    {isSubmittingComment ? "Sending..." : "Send"}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Existing Comments Section */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "var(--primary-base-color)",
                  }}
                >
                  Previous Notes & Comments
                </Typography>

                {loadingComments ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : existingComments.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {existingComments.map((commentItem) => (
                      <Card
                        key={commentItem.id}
                        variant="outlined"
                        sx={{
                          borderRadius: "10px",
                          border: "1px solid #e0e0e0",
                          bgcolor:
                            commentItem.type === "note" ? "#F8F9FA" : "#FFFFFF",
                        }}
                      >
                        <CardContent sx={{ py: 2, px: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {commentItem.expertImage && (
                                <Avatar
                                  src={commentItem.expertImage}
                                  onError={(e) => {
                                    const newLink = handleBrokenImage(
                                      commentItem.expertImage
                                    );
                                    e.target.src = newLink;
                                  }}
                                  sx={{ width: 24, height: 24 }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  color: "var(--primary-base-color)",
                                }}
                              >
                                {commentItem.author}
                              </Typography>
                              <Chip
                                label={
                                  commentItem.type === "note"
                                    ? "Note"
                                    : "Comment"
                                }
                                size="small"
                                sx={{
                                  bgcolor:
                                    commentItem.type === "note"
                                      ? "#E3F2FD"
                                      : "#F3E5F5",
                                  color:
                                    commentItem.type === "note"
                                      ? "#1976D2"
                                      : "#7B1FA2",
                                  fontSize: "10px",
                                  height: "20px",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {commentItem.timestamp}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            {commentItem.text}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      bgcolor: "#F8F9FA",
                      borderRadius: 2,
                      border: "1px dashed #D0D0D0",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No notes or comments yet. Add the first one above!
                    </Typography>
                  </Box>
                )}

                {/* Pagination Controls */}
                {existingComments.length > 0 && totalCommentsPages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<NavigateBefore />}
                      onClick={handlePreviousPage}
                      disabled={commentsPage <= 1}
                      size="small"
                      sx={{
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        borderColor: "var(--primary-base-color)",
                        color: "var(--primary-base-color)",
                        "&:hover": {
                          borderColor: "var(--primary-base-color-darken)",
                          bgcolor: "rgba(15, 103, 110, 0.04)",
                        },
                        "&:disabled": {
                          borderColor: "#ccc",
                          color: "#ccc",
                        },
                      }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Page {commentsPage} of {totalCommentsPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<NavigateNext />}
                      onClick={handleNextPage}
                      disabled={commentsPage >= totalCommentsPages}
                      size="small"
                      sx={{
                        borderRadius: "10px",
                        textTransform: "capitalize",
                        borderColor: "var(--primary-base-color)",
                        color: "var(--primary-base-color)",
                        "&:hover": {
                          borderColor: "var(--primary-base-color-darken)",
                          bgcolor: "rgba(15, 103, 110, 0.04)",
                        },
                        "&:disabled": {
                          borderColor: "#ccc",
                          color: "#ccc",
                        },
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={openCancelConfirmationModal}
        onClose={handleCancelConfirmationClose}
        sx={{
          maxWidth: "700px",
          width: "98%",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {isCancelled !== null && !isCancelled && (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "rgb(183 216 237 / 58%)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: "10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </div>
        )}
        <DialogTitle sx={{ fontWeight: "bold", color: "#f57e8c" }}>
          Cancel Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#666", fontSize: "16px" }}>
            Are you sure you want to cancel this appointment with{" "}
            <strong>{patientData?.name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ color: "#666", fontSize: "14px", mt: 2 }}>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCancelConfirmationClose}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "capitalize",
            }}
          >
            Keep Appointment
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            sx={{
              bgcolor: "#f57e8c",
              color: "white",
              borderRadius: "10px",
              textTransform: "capitalize",
              "&:hover": {
                bgcolor: "#d32f2f",
              },
            }}
          >
            Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog
        open={openRescheduleModal}
        onClose={handleRescheduleClose}
        sx={{
          maxWidth: "700px",
          width: "95%",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {isRescheduled !== null && !isRescheduled && (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "rgb(183 216 237 / 58%)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: "10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </div>
        )}
        <DialogTitle
          sx={{ fontWeight: "bold", color: "var(--primary-base-color)" }}
        >
          Reschedule Appointment
        </DialogTitle>
        <DialogContent sx={{ minWidth: "600px", maxWidth: "700px" }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Patient: {patientData?.name}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
              New Date
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={
                    rescheduleData.appointmentDate
                      ? dayjs(
                          rescheduleData.appointmentDate,
                          "DD/MM/YYYY"
                        ).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={(e) => onDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Available Slots Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
              Available Time Slots
            </Typography>

            {!rescheduleData.appointmentDate ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  bgcolor: "#F8F9FA",
                  borderRadius: 2,
                  border: "1px dashed #D0D0D0",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Please select a date to view available time slots
                </Typography>
              </Box>
            ) : loadingSlots ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2 }}
                >
                  Fetching available slots...
                </Typography>
              </Box>
            ) : availableSlots.length > 0 ? (
              <Box>
                <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                  Found {availableSlots.length} available slot(s) for{" "}
                  {rescheduleData.appointmentDate}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {availableSlots.map((slot, index) => (
                    <Chip
                      key={index}
                      label={slot}
                      onClick={() => onTimeChange(slot)}
                      variant={
                        rescheduleData.appointmentTime === slot
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        borderRadius: "10px",
                        cursor: "pointer",
                        bgcolor:
                          rescheduleData.appointmentTime === slot
                            ? "var(--primary-base-color)"
                            : "transparent",
                        color:
                          rescheduleData.appointmentTime === slot
                            ? "white"
                            : "var(--primary-base-color)",
                        borderColor: "var(--primary-base-color)",
                        "&:hover": {
                          bgcolor:
                            rescheduleData.appointmentTime === slot
                              ? "var(--primary-base-color-darken)"
                              : "rgba(15, 103, 110, 0.04)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  bgcolor: "#F8F9FA",
                  borderRadius: 2,
                  border: "1px dashed #D0D0D0",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {rescheduleData.appointmentDate
                    ? `No available slots for ${rescheduleData.appointmentDate}`
                    : "Please select a date to view available slots"}
                </Typography>
                {rescheduleData.appointmentDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Try selecting a different date
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleRescheduleClose}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "capitalize",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRescheduleAppointment}
            variant="contained"
            disabled={
              !rescheduleData.appointmentDate || !rescheduleData.appointmentTime
            }
            sx={{
              bgcolor: "var(--primary-base-color)",
              color: "white",
              borderRadius: "10px",
              textTransform: "capitalize",
              "&:hover": {
                bgcolor: "var(--primary-base-color-darken)",
              },
              "&:disabled": {
                bgcolor: "#ccc",
                color: "#666",
              },
            }}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppointmentDetailsViewPage;
